import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertCircle, CheckCircle, Database, Eye, EyeOff, RefreshCw, FileText, TrendingUp, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataDebugView = ({ tenantId }) => {
  const [debugData, setDebugData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  const fetchDebugData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/agents/debug/data-context/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch debug data: ${errorText}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setDebugData(data.debug);
      } else {
        throw new Error(data.message || 'Unknown error');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchDebugData();
    }
  }, [tenantId]);

  const getStatusBadge = (status) => {
    if (status === 'AVAILABLE') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Available</Badge>;
    } else if (status === 'NO_DATA') {
      return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="w-3 h-3 mr-1" /> No Data</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" /> Error</Badge>;
    }
  };

  const formatNumber = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
            <span className="text-blue-700">Loading debug data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>Error: {error}</span>
          </div>
          <Button 
            onClick={fetchDebugData} 
            variant="outline" 
            size="sm" 
            className="mt-3"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!debugData) return null;

  const salesAnalytics = debugData.sample_analytics?.sales || {};
  const invAnalytics = debugData.sample_analytics?.inventory || {};
  const execSummary = salesAnalytics.executive_summary || {};
  const invHealth = invAnalytics.health || {};

  return (
    <Card className="border-slate-200" data-testid="debug-view-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5 text-slate-600" />
              Data Debug View
            </CardTitle>
            <CardDescription>
              Shows exactly what data is sent to the LLM for analysis
            </CardDescription>
          </div>
          <Button 
            onClick={fetchDebugData} 
            variant="outline" 
            size="sm"
            data-testid="refresh-debug-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Data Availability Grid */}
        <div>
          <h4 className="font-medium text-sm text-slate-700 mb-3">Canonical Dataset Availability</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(debugData.data_availability || {}).map(([dataset, status]) => (
              <div 
                key={dataset}
                className="p-2 rounded-lg border bg-white flex flex-col items-center text-center"
              >
                <span className="text-xs font-mono text-slate-600 mb-1">{dataset}</span>
                {getStatusBadge(status)}
                <span className="text-xs text-slate-500 mt-1">
                  {(debugData.record_counts?.[dataset] || 0).toLocaleString()} rows
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics Summary */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Sales Analytics */}
          {salesAnalytics.top_stores && (
            <div className="p-4 rounded-lg border bg-gradient-to-br from-blue-50 to-indigo-50">
              <h4 className="font-medium text-sm flex items-center gap-2 text-blue-800 mb-3">
                <TrendingUp className="w-4 h-4" />
                Sales Analytics (Sent to LLM)
              </h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Revenue:</span>
                  <span className="font-medium">{execSummary.total_revenue_formatted || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Units:</span>
                  <span className="font-medium">{execSummary.total_units?.toLocaleString() || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">ASP:</span>
                  <span className="font-medium">{execSummary.asp_formatted || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Gross Margin:</span>
                  <span className="font-medium">{execSummary.gross_margin_pct?.toFixed(1)}%</span>
                </div>
                
                <div className="pt-2 border-t mt-2">
                  <span className="text-slate-600">Top Stores:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {salesAnalytics.top_stores?.map(s => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <span className="text-slate-600">Top Products:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {salesAnalytics.top_products?.slice(0, 5).map(p => (
                      <Badge key={p} variant="outline" className="text-xs font-mono">{p}</Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between pt-2">
                  <span className="text-slate-600">Anomalies Detected:</span>
                  <Badge className={salesAnalytics.anomalies_detected > 0 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                    {salesAnalytics.anomalies_detected || 0}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Inventory Analytics */}
          {invAnalytics.summary && (
            <div className="p-4 rounded-lg border bg-gradient-to-br from-amber-50 to-orange-50">
              <h4 className="font-medium text-sm flex items-center gap-2 text-amber-800 mb-3">
                <Package className="w-4 h-4" />
                Inventory Analytics (Sent to LLM)
              </h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Stock Value:</span>
                  <span className="font-medium">{invAnalytics.summary?.total_stock_val_formatted || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Units:</span>
                  <span className="font-medium">{invAnalytics.summary?.total_stock_qty?.toLocaleString() || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Active Stores:</span>
                  <span className="font-medium">{invAnalytics.summary?.unique_stores || 'N/A'}</span>
                </div>
                
                <div className="pt-2 border-t mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Health Score:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">{invHealth.score || 'N/A'}</span>
                      <Badge className={
                        invHealth.status === 'HEALTHY' ? 'bg-green-100 text-green-800' :
                        invHealth.status === 'AT_RISK' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {invHealth.status || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-600">Fresh Stock (0-30d):</span>
                  <span className="font-medium text-green-700">{invHealth.fresh_stock_pct?.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Aged Stock (120+d):</span>
                  <span className="font-medium text-red-700">{invHealth.aged_stock_pct?.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* LLM Prompt Preview */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-sm flex items-center gap-2 text-slate-700">
              <FileText className="w-4 h-4" />
              LLM Prompt Preview ({debugData.llm_prompt_total_chars?.toLocaleString()} chars)
            </h4>
            <Button 
              onClick={() => setShowPromptPreview(!showPromptPreview)}
              variant="ghost" 
              size="sm"
              data-testid="toggle-prompt-preview-btn"
            >
              {showPromptPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {showPromptPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </div>
          
          {showPromptPreview && (
            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-auto max-h-96">
              <pre className="whitespace-pre-wrap">{debugData.llm_prompt_preview}</pre>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataDebugView;
